export interface ModuleStep {
  id: string;
  label: string;
  description: string;
  required: boolean;
  component: string;
  validation?: string[];
}

export const MODULE_BUILDER_STEPS: ModuleStep[] = [
  {
    id: "module-basics",
    label: "Module Basics",
    description: "Configure module size and basic parameters",
    required: true,
    component: "ModuleBasicsStep",
    validation: ["size", "techLevel"],
  },
  {
    id: "module-drives",
    label: "Drives",
    description: "Install drive systems for the module",
    required: false,
    component: "ModuleDrivesStep",
    validation: [],
  },
  {
    id: "module-power",
    label: "Power Plant",
    description: "Install power plant for the module",
    required: false,
    component: "ModulePowerStep",
    validation: [],
  },
  {
    id: "module-fuel",
    label: "Fuel Tanks",
    description: "Configure fuel storage for the module",
    required: false,
    component: "ModuleFuelStep",
    validation: [],
  },
  {
    id: "module-computer",
    label: "Computer",
    description: "Install computer system for the module",
    required: false,
    component: "ModuleComputerStep",
    validation: [],
  },
  {
    id: "module-sensors",
    label: "Sensors",
    description: "Install sensor systems for the module",
    required: false,
    component: "ModuleSensorsStep",
    validation: [],
  },
  {
    id: "module-weapons",
    label: "Weapons",
    description: "Install weapons and defensive systems",
    required: false,
    component: "ModuleWeaponsStep",
    validation: [],
  },
  {
    id: "module-systems",
    label: "Optional Systems",
    description: "Add optional systems and equipment to the module",
    required: false,
    component: "ModuleSystemsStep",
    validation: [],
  },
  {
    id: "module-cargo",
    label: "Cargo Configuration",
    description: "Configure cargo capacity and storage systems",
    required: false,
    component: "ModuleCargoStep",
    validation: [],
  },
  {
    id: "module-calculations",
    label: "Module Calculations",
    description: "Review module statistics and calculations",
    required: true,
    component: "ModuleCalculationsStep",
    validation: ["calculations"],
  },
  {
    id: "module-export",
    label: "Export Module",
    description: "Save and export the module design",
    required: true,
    component: "ModuleExportStep",
    validation: ["name", "export"],
  },
];

export const MODULE_TYPES = {
  cargo: {
    name: "Cargo Module",
    description: "Standard cargo storage module",
    baseCost: 25000, // Cr25,000 per ton as per Traveller rules
    maxSize: 1000,
    minSize: 10,
  },
  passenger: {
    name: "Passenger Module",
    description: "Passenger accommodation module",
    baseCost: 25000, // Cr25,000 per ton as per Traveller rules
    maxSize: 500,
    minSize: 20,
  },
  military: {
    name: "Military Module",
    description: "Military equipment and weapons module",
    baseCost: 25000, // Cr25,000 per ton as per Traveller rules
    maxSize: 2000,
    minSize: 50,
  },
  utility: {
    name: "Utility Module",
    description: "General purpose utility module",
    baseCost: 25000, // Cr25,000 per ton as per Traveller rules
    maxSize: 1500,
    minSize: 15,
  },
  custom: {
    name: "Custom Module",
    description: "Custom module design",
    baseCost: 25000, // Cr25,000 per ton as per Traveller rules
    maxSize: 1000,
    minSize: 10,
  },
} as const;

export const MODULE_VALIDATION_RULES = {
  size: {
    min: 0,
    message: "Module size must be at least 0 tons",
  },
  techLevel: {
    min: 0,
    message: "Technology level must be at least 0",
  },
  name: {
    minLength: 3,
    maxLength: 50,
    message: "Module name must be between 3 and 50 characters",
  },
  systems: {
    maxOptionalSystems: 20,
    message: "Module cannot have more than 20 optional systems",
  },
} as const;

// Modular Hull Rules as per Traveller Core Rulebook
export const MODULAR_HULL_RULES = {
  maxModularPercentage: 75, // Up to 75% of ship's internal tonnage can be modular
  moduleCostPerTon: 25000, // Cr25,000 per ton for modules (as per official rules)
  modularHullCostMultiplier: 1.0, // Hull cost increases by modular percentage (e.g., 30% modular = 130% hull cost)
  
  // Systems that CANNOT be modular (must be in main hull)
  nonModularSystems: [
    "bridge",
    "structure",
    "armor"
  ],
  
  // Systems that CAN be modular (updated based on official example)
  modularSystems: [
    "optionalSystems",
    "weapons",
    "weaponMounts",
    "defensiveSystems",
    "ammunitionStorage",
    "cargo",
    "fuel",
    "staterooms",
    "accommodations",
    "laboratories",
    "hangarSpace",
    "missileBays",
    "powerPlant", // Modules can have their own power plants (e.g., Pressurised Shelter)
    "computer", // Modules can have computers (e.g., Pressurised Shelter)
    "sensors", // Modules can have sensors (e.g., Gunship Module)
    "software", // Modules can have software
    "drives", // Modules can have their own drives
    "dockingSpace", // Vehicle cradle functionality
    "accelerationSeats", // Personnel transport
    "diggerBlades", // Assault boat functionality
    "dockingClamps", // Fighter frame functionality
    "unrepSystem", // Fuel skimmer functionality
  ],
  
  // Firmpoints/Hardpoints calculation rules
  firmpointsRules: {
    calculateSeparately: true, // Calculate separately for main hull and modules
    totalCannotExceed: true, // Total cannot exceed non-modular ship limits
  }
} as const;

export type ModuleType = keyof typeof MODULE_TYPES;
export type ModuleStepId = typeof MODULE_BUILDER_STEPS[number]["id"];
